import React from 'react';
import { formatBytes } from '../photoUpload/caps';

// The pooled-storage meter, extracted from PlanBillingSection (#349) so the
// storage-full upsell prompt (#350) shows the SAME meter rather than a second
// rendering of the same numbers that can drift from it.
//
// Deliberately presentational: no hooks, no listeners. Callers already hold the
// usage (useAccountStorage on the account page, caps.poolUsedBytes in the upload
// preflight) and passing it in avoids a second rollup fetch inside a modal.
//
// Class names stay `plan-meter*` — the account page already styles them, and
// UpgradePromptDialog.css re-declares them scoped under its own root so the
// meter is styled inside the modal too, without either surface's rules leaking
// into the other.
function StorageMeter({
  usedBytes,
  totalBytes,
  label = 'Storage',
  // The label has to name what the number actually counts. It used to say
  // "Photos", which was true of the old photo-only rollup and wrong the moment
  // source videos and renders entered the pool (#412) — a user watching a
  // "photos" meter not move after deleting 21 GB of video reasonably concludes
  // the product is broken.
  hint = 'Photos, source videos, and compiled videos across all your projects.',
  testId = 'storage-meter',
}) {
  const known = Number.isFinite(usedBytes) && Number.isFinite(totalBytes) && totalBytes > 0;
  const percent = known
    ? Math.min(100, Math.round((usedBytes / totalBytes) * 100))
    : 0;
  const totalLabel = Number.isFinite(totalBytes) ? formatBytes(totalBytes) : 'your plan’s pool';

  return (
    <div className="plan-meter" data-testid={testId}>
      <div className="plan-meter__head">
        <span className="plan-meter__label">{label}</span>
        <span className="plan-meter__value">
          {Number.isFinite(usedBytes)
            ? `${formatBytes(usedBytes)} of ${totalLabel}`
            : `calculating… of ${totalLabel}`}
        </span>
      </div>
      <div className="plan-meter__track">
        <div
          className="plan-meter__fill"
          data-testid={`${testId}-fill`}
          style={{ width: `${percent}%` }}
        />
      </div>
      {hint && <p className="plan-meter__hint">{hint}</p>}
    </div>
  );
}

export default StorageMeter;
